import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Headset, MessageSquare, Send, Wand2, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SEO } from '@/components/landing/shared/SEO';
import { cn } from '@/lib/utils';

const INQUIRY_OPTIONS = [
    { value: 'Technical Support', label: 'Technical Support' },
    { value: 'Enterprise Sales', label: 'Enterprise Sales' },
    { value: 'Professional Feedback', label: 'Feedback' },
    { value: 'Other Concerns', label: 'Other' },
] as const;

interface ContactPageProps {
    isDashboard?: boolean;
}

const ContactPage = ({ isDashboard = false }: ContactPageProps) => {
    const [searchParams] = useSearchParams();
    const inquiryFromUrl = searchParams.get('inquiry');
    const [inquiryType, setInquiryType] = useState<string>(() =>
        inquiryFromUrl === 'sales' ? 'Enterprise Sales' : 'Technical Support'
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (inquiryFromUrl === 'sales') setInquiryType('Enterprise Sales');
    }, [inquiryFromUrl]);

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSupportClick = () => {
        setInquiryType('Technical Support');
        scrollToForm();
    };

    const handleEnterpriseClick = () => {
        setInquiryType('Enterprise Sales');
        scrollToForm();
    };

    const handleCardKeyDown = (e: React.KeyboardEvent, action: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action();
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const formData = new FormData(form);

        // Add FormSubmit config (no _next = no redirect)
        formData.append('_subject', 'New ChytBot Inquiry');
        formData.append('_template', 'table');
        formData.append('_captcha', 'false');

        setIsSubmitting(true);
        setSubmitSuccess(false);

        try {
            let formsubmitUrl = import.meta.env.VITE_FORMSUBMIT_URL
            if (!formsubmitUrl) {
                toast.error('Contact form not configured');
                setIsSubmitting(false);
                return;
            }
            
            // FormSubmit requires /ajax/ path for fetch requests to avoid CORS issues
            if (formsubmitUrl.includes('formsubmit.co/') && !formsubmitUrl.includes('/ajax/')) {
                formsubmitUrl = formsubmitUrl.replace('formsubmit.co/', 'formsubmit.co/ajax/');
            }
            
            const res = await fetch(formsubmitUrl, {
                method: 'POST',
                body: formData,
                headers: { Accept: 'application/json' },
            });

            if (res.ok) {
                posthog.capture('contact_form_submitted', { inquiry_type: inquiryType });
                setSubmitSuccess(true);
                toast.success('Message sent successfully', {
                    description: "We'll get back to you as soon as possible.",
                });
                form.reset();
                setInquiryType(inquiryFromUrl === 'sales' ? 'Enterprise Sales' : 'Technical Support');
            } else {
                throw new Error('Submission failed');
            }
        } catch (err) {
            Sentry.captureException(err, { extra: { action: 'contactFormSubmit', inquiryType } });
            toast.error('Failed to send message', {
                description: 'Please try again or email us directly.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={cn(
            "relative min-h-screen bg-background overflow-hidden selection:bg-primary/20 selection:text-foreground",
            isDashboard ? "pt-10 pb-10" : "pt-32 pb-20"
        )}>
            <SEO
                title="Contact Us - AI Chatbot Support & Enterprise Sales"
                description="Contact ChytBot for technical support, enterprise solutions, and sales inquiries. US-based support team ready to help your business."
                url="https://chytbot.com/contact"
                keywords={[
                    "contact ChytBot",
                    "chatbot support",
                    "chatbot customer service",
                    "AI chatbot for customer service",
                    "enterprise chatbot sales",
                    "AI chatbot help",
                    "customer support contact",
                    "ChytBot support USA",
                    "chatbot enterprise solutions",
                    "white label chatbot",
                    "custom chatbot development"
                ]}
                structuredData={[
                    {
                        "@context": "https://schema.org",
                        "@type": "ContactPage",
                        "name": "Contact ChytBot",
                        "description": "Get in touch with ChytBot for support and enterprise inquiries",
                        "url": "https://chytbot.com/contact",
                        "mainEntity": {
                            "@type": "Organization",
                            "name": "ChytBot",
                            "contactPoint": [
                                {
                                    "@type": "ContactPoint",
                                    "contactType": "technical support",
                                    "availableLanguage": "English",
                                    "areaServed": "US"
                                },
                                {
                                    "@type": "ContactPoint",
                                    "contactType": "sales",
                                    "availableLanguage": "English",
                                    "areaServed": "US"
                                }
                            ]
                        }
                    }
                ]}
            />

            {/* Premium Background Ornaments */}
            {!isDashboard && (
                <>
                    <div className="absolute top-0 left-0 w-full h-[800px] bg-gradient-to-b from-primary/10 via-background to-transparent pointer-events-none -z-10" />
                    <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none -z-10 animate-pulse duration-[10000ms]" />
                    <div className="absolute bottom-[20%] left-[-10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none -z-10" />
                </>
            )}

            <div className={cn("container relative px-4 mx-auto", isDashboard ? "max-w-5xl" : "max-w-6xl")}>
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-center">
                        {/* Left Side: Editorial Style Info */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="space-y-12"
                        >
                            <div className="space-y-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest"
                                >
                                    <MessageSquare className="w-3 h-3" />
                                    Get In Touch
                                </motion.div>
                                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.9] text-foreground">
                                    IMPROVE <br />
                                    <span className="text-muted-foreground">Your Support.</span>
                                </h1>
                                <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-md">
                                    We bridge the gap between human curiosity and automated precision. Reach out for support or enterprise inquiries.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleSupportClick}
                                    onKeyDown={(e) => handleCardKeyDown(e, handleSupportClick)}
                                    className="space-y-4 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-2xl"
                                    aria-label="Go to form for Technical Support"
                                >
                                    <div className="w-12 h-12 rounded-2xl bg-muted/30 flex items-center justify-center text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-all duration-300 border border-border/50 shadow-inner overflow-hidden relative">
                                        <div className="absolute inset-0 bg-primary/5 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <Headset className="w-6 h-6 relative z-10" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm uppercase tracking-widest text-foreground">Support</h3>
                                        <p className="text-sm text-muted-foreground mt-1">Direct technical assistance for your deployment.</p>
                                        <p className="text-[10px] font-medium text-primary mt-2">Get in touch →</p>
                                    </div>
                                </div>

                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleEnterpriseClick}
                                    onKeyDown={(e) => handleCardKeyDown(e, handleEnterpriseClick)}
                                    className="space-y-4 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-2xl"
                                    aria-label="Go to form for Enterprise Sales"
                                >
                                    <div className="w-12 h-12 rounded-2xl bg-muted/30 flex items-center justify-center text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-all duration-300 border border-border/50 shadow-inner overflow-hidden relative">
                                        <div className="absolute inset-0 bg-primary/5 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <Wand2 className="w-6 h-6 relative z-10" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm uppercase tracking-widest text-foreground">Enterprise</h3>
                                        <p className="text-sm text-muted-foreground mt-1">White-label and custom training solutions.</p>
                                        <p className="text-[10px] font-medium text-primary mt-2">Get in touch →</p>
                                    </div>
                                </div>
                            </div>

                        </motion.div>

                        {/* Right Side: Floating Glassmorphic Form */}
                        <motion.div
                            ref={formRef}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
                            className="relative group lg:mt-0 mt-8 scroll-mt-24"
                        >
                            {/* Decorative glow behind card */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-muted-foreground/20 rounded-[2.5rem] blur-2xl opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />

                            <div className="relative bg-card/60 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-6 sm:p-8 md:p-10 shadow-2xl">
                                {submitSuccess ? (
                                    <div className="flex flex-col items-center justify-center py-12 space-y-6 text-center">
                                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                            <CheckCircle className="w-8 h-8 text-emerald-500" />
                                        </div>
                                        <div className="space-y-2">
                                            <h3 className="text-xl font-bold text-foreground">Message sent successfully</h3>
                                            <p className="text-muted-foreground">We&apos;ll get back to you as soon as possible.</p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={() => setSubmitSuccess(false)}
                                        >
                                            Send another message
                                        </Button>
                                    </div>
                                ) : (
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    {/* Honeypot for spam */}
                                    <input type="text" name="_honey" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="text-[10px] font-bold uppercase tracking-widest ml-1 text-muted-foreground">Name</Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                placeholder="Your Name"
                                                required
                                                className="h-12 bg-muted/20 border-border/30 focus:border-primary/50 focus:ring-primary/20 rounded-xl px-4 transition-all"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="text-[10px] font-bold uppercase tracking-widest ml-1 text-muted-foreground">Email</Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                placeholder="hello@company.com"
                                                required
                                                className="h-12 bg-muted/20 border-border/30 focus:border-primary/50 focus:ring-primary/20 rounded-xl px-4 transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="reason" className="text-[10px] font-bold uppercase tracking-widest ml-1 text-muted-foreground">Inquiry Nature</Label>
                                        <select
                                            id="reason"
                                            name="inquiry_type"
                                            value={inquiryType}
                                            onChange={(e) => setInquiryType(e.target.value)}
                                            className="flex h-12 w-full rounded-xl border border-border/30 bg-muted/20 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-all text-foreground appearance-none"
                                            required
                                        >
                                            {INQUIRY_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value} className="bg-background">{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="subject" className="text-[10px] font-bold uppercase tracking-widest ml-1 text-muted-foreground">Subject</Label>
                                        <Input
                                            id="subject"
                                            name="subject"
                                            placeholder="Brief summary of your request"
                                            className="h-12 bg-muted/20 border-border/30 focus:border-primary/50 focus:ring-primary/20 rounded-xl px-4 transition-all"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="message" className="text-[10px] font-bold uppercase tracking-widest ml-1 text-muted-foreground">Message</Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            placeholder="How can we assist your business today?"
                                            required
                                            className="min-h-[140px] bg-muted/20 border-border/30 focus:border-primary/50 focus:ring-primary/20 rounded-xl px-4 py-4 transition-all leading-relaxed"
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        size="lg"
                                        disabled={isSubmitting}
                                        className="w-full font-bold h-14 rounded-xl group relative overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Request
                                                    <Send className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                                                </>
                                            )}
                                        </span>
                                    </Button>
                                </form>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
